import { FC, forwardRef, ReactElement, Ref, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppBar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  SlideProps,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';

import { Button, ButtonProps, Icon } from '@components/base';
import { IconOption } from '@components/base/dataDisplay/Icon';
import { IndexName } from '@enums/IndexName';
import useFilterDefsQuery from '@hooks/queryHooks/useFilterDefsQuery';
import { Title } from '@models/Title';
import SearchAndFilterProvider from '@modules/layouts/hooks/useSearchAndFilter/provider';
import { generateFilter } from '@utils/filterUtils';

import TitleSearch from './TitleSearch';

const Transition = forwardRef(
  (props: SlideProps & { children?: ReactElement }, ref: Ref<unknown>) => (
    <Slide direction='up' ref={ref} {...props} />
  )
);

Transition.displayName = 'Transition';

type TitleSelectorProps = {
  actionColor?: ButtonProps['color'];
  actionIcon?: IconOption;
  actionLabel: string;
  currentTitle?: Title;
  heading: string;
  onDismiss: () => void;
  onSelect: (title: Title) => void;
  show: boolean;
  type: string;
};

const TitleSelector: FC<TitleSelectorProps> = ({
  actionColor = 'inherit',
  actionIcon,
  actionLabel,
  currentTitle,
  heading,
  onDismiss,
  onSelect,
  show,
}) => {
  const { t } = useTranslation();
  const { data: filterDefs } = useFilterDefsQuery(IndexName.Assets);

  const [selectedTitle, setSelectedTitle] = useState<Title>();
  const [compatibilityDialogOpen, setCompatibilityDialogOpen] = useState(false);

  const handleSelectTitle = (title: Title) => {
    if (title) {
      if (title.masterId === selectedTitle?.masterId) setSelectedTitle(undefined);
      else if (title.masterId !== currentTitle?.masterId) setSelectedTitle(title);
    }
  };

  const handleSelect = () => {
    if (currentTitle && selectedTitle) {
      const types = [currentTitle.type, selectedTitle.type];
      if (
        types.every(val => val === types[0]) ||
        (types.includes('program') && (types.includes('movie') || types.includes('series')))
      )
        onSelect(selectedTitle);
      else setCompatibilityDialogOpen(true);
    }
  };

  return (
    <Dialog
      fullScreen
      onClose={onDismiss}
      open={show}
      scroll='paper'
      slots={{ transition: Transition }}
    >
      <AppBar position='sticky'>
        <Stack alignItems='center' component={Toolbar} direction='row' spacing={1}>
          <IconButton aria-label='close' color='inherit' edge='start' onClick={onDismiss}>
            <Icon name='close' />
          </IconButton>

          <Typography flex={1} variant='h4'>
            {heading}
          </Typography>

          <Button
            autoFocus
            color={actionColor}
            disabled={!selectedTitle}
            leftIcon={actionIcon}
            onClick={handleSelect}
            variant='contained'
          >
            {actionLabel}
          </Button>
        </Stack>
      </AppBar>

      {currentTitle && (
        <SearchAndFilterProvider
          searchQuery={{
            filter: filterDefs
              ? generateFilter(filterDefs, 'Type', [currentTitle.type])
              : undefined,
            queryText: currentTitle.titleName,
          }}
        >
          <TitleSearch
            currentTitle={currentTitle}
            disableHeading
            selectedTitle={selectedTitle}
            setSelectedTitle={handleSelectTitle}
          />
        </SearchAndFilterProvider>
      )}

      {compatibilityDialogOpen && selectedTitle && currentTitle && (
        <Dialog fullWidth maxWidth='sm' onClose={() => setCompatibilityDialogOpen(false)} open>
          <DialogTitle>{t('titleSelector.incompatible.heading')}</DialogTitle>

          <DialogContent>
            <Trans
              components={[<strong />]}
              i18nKey='titleSelector.incompatible.description'
              values={{
                currentTitle: currentTitle.titleName,
                currentTitleType: currentTitle.type,
                selectedTitle: selectedTitle.titleName,
                selectedTitleType: selectedTitle.type,
              }}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setCompatibilityDialogOpen(false)}>{t('ok')}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default TitleSelector;
